import React from 'react';
import { Label } from '../ui/label';
import { ArrowLeft, ArrowRight, Check, CloudUpload, X, FileText } from 'lucide-react';

export const Step2FileUpload = ({ form, setForm, onBack, onContinue }) => {
  const handleFileChange = (e, type) => {
    const newFiles = Array.from(e.target.files || []);
    if (type === 'photos') {
      setForm({ ...form, photographFiles: [...form.photographFiles, ...newFiles], hasPhotos: true });
    } else if (type === 'plans') {
      setForm({ ...form, planFiles: [...form.planFiles, ...newFiles], hasScaledDrawings: true });
    } else if (type === 'supporting') {
      setForm({ ...form, supportingFiles: [...(form.supportingFiles || []), ...newFiles] });
    }
  };

  const removeFile = (index, type) => {
    if (type === 'photos') {
      const newFiles = [...form.photographFiles];
      newFiles.splice(index, 1);
      setForm({ ...form, photographFiles: newFiles, hasPhotos: newFiles.length > 0 });
    } else if (type === 'plans') {
      const newFiles = [...form.planFiles];
      newFiles.splice(index, 1);
      setForm({ ...form, planFiles: newFiles, hasScaledDrawings: newFiles.length > 0 });
    } else if (type === 'supporting') {
      const newFiles = [...(form.supportingFiles || [])];
      newFiles.splice(index, 1);
      setForm({ ...form, supportingFiles: newFiles });
    }
  };

  const canContinue = form.planFiles.length > 0 && form.photographFiles.length > 0;
  const supportingCount = (form.supportingFiles || []).length;

  const FileList = ({ files, type }) => (
    <div className="space-y-2 mt-2">
      {files.map((file, index) => (
        <div 
          key={`${file.name}-${index}`} 
          className="flex items-center justify-between p-3 rounded-lg" 
          style={{ backgroundColor: 'white', border: '1px solid var(--color-border)' }}
        >
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4" style={{ color: 'var(--color-navy)' }} />
            <span className="text-sm" style={{ color: 'var(--color-text)' }}>{file.name}</span>
            <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
              ({(file.size / 1024).toFixed(1)} KB)
            </span>
          </div>
          <button 
            onClick={() => removeFile(index, type)} 
            className="transition-colors hover:text-red-500" 
            style={{ color: 'var(--color-muted)' }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );

  return (
    <div className="card" data-testid="step-2-card">
      <h2 className="text-2xl font-semibold mb-2" style={{ color: 'var(--color-navy)' }}>
        Upload Project Files
      </h2>
      <p className="text-sm mb-6" style={{ color: 'var(--color-muted)' }}>
        Upload your drawings, photographs and any supporting documents. Plans and photos are required.
      </p>

      <div className="space-y-6">
        {/* Plans Upload */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div 
              className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold" 
              style={{
                backgroundColor: form.planFiles.length > 0 ? 'var(--color-navy)' : 'var(--color-border)',
                color: form.planFiles.length > 0 ? 'white' : 'var(--color-muted)'
              }}
            >
              {form.planFiles.length > 0 ? <Check className="w-4 h-4" /> : '1'}
            </div>
            <span className="font-semibold" style={{ color: 'var(--color-text)' }}>
              Scaled Drawings / Plans *
            </span>
          </div>
          <div 
            className="upload-zone cursor-pointer" 
            onClick={() => document.getElementById('planUpload').click()} 
            data-testid="plan-upload-zone"
          >
            <CloudUpload className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--color-navy)' }} />
            <p className="font-medium" style={{ color: 'var(--color-text)' }}>Click to upload drawings / plans</p>
            <p className="text-sm mt-1" style={{ color: 'var(--color-muted)' }}>PDF, DWG, PNG, JPG up to 10MB each</p>
            <input
              id="planUpload"
              type="file"
              multiple
              onChange={(e) => handleFileChange(e, 'plans')}
              className="hidden"
              accept=".pdf,.dwg,.png,.jpg,.jpeg"
            />
          </div>
          {form.planFiles.length > 0 && <FileList files={form.planFiles} type="plans" />}
        </div>

        {/* Photos Upload */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div 
              className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold" 
              style={{
                backgroundColor: form.photographFiles.length > 0 ? 'var(--color-navy)' : 'var(--color-border)',
                color: form.photographFiles.length > 0 ? 'white' : 'var(--color-muted)'
              }}
            >
              {form.photographFiles.length > 0 ? <Check className="w-4 h-4" /> : '2'}
            </div>
            <span className="font-semibold" style={{ color: 'var(--color-text)' }}>
              Site Photographs *
            </span>
          </div>
          <div 
            className="upload-zone cursor-pointer" 
            onClick={() => document.getElementById('photoUpload').click()} 
            data-testid="photo-upload-zone"
          >
            <CloudUpload className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--color-navy)' }} />
            <p className="font-medium" style={{ color: 'var(--color-text)' }}>Click to upload photographs</p>
            <p className="text-sm mt-1" style={{ color: 'var(--color-muted)' }}>JPG, PNG, HEIC up to 10MB each</p>
            <input
              id="photoUpload"
              type="file"
              multiple
              onChange={(e) => handleFileChange(e, 'photos')}
              className="hidden"
              accept=".jpg,.jpeg,.png,.heic"
            />
          </div>
          {form.photographFiles.length > 0 && <FileList files={form.photographFiles} type="photos" />}
        </div>

        {/* Supporting Documents */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div 
              className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold" 
              style={{
                backgroundColor: supportingCount > 0 ? 'var(--color-navy)' : 'var(--color-border)',
                color: supportingCount > 0 ? 'white' : 'var(--color-muted)'
              }}
            >
              {supportingCount > 0 ? <Check className="w-4 h-4" /> : '3'}
            </div>
            <span className="font-semibold" style={{ color: 'var(--color-text)' }}>
              Supporting Documents
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--color-bg)', color: 'var(--color-muted)' }}>Optional</span>
          </div>
          <p className="text-sm" style={{ color: 'var(--color-muted)', marginTop: -4 }}>
            Specifications, structural reports, utility quotations and the like or any other tender pack documents.
          </p>
          <div 
            className="upload-zone cursor-pointer" 
            onClick={() => document.getElementById('supportingUpload').click()} 
            data-testid="supporting-upload-zone"
          >
            <FileText className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--color-navy)' }} />
            <p className="font-medium" style={{ color: 'var(--color-text)' }}>Click to upload supporting documents</p>
            <p className="text-sm mt-1" style={{ color: 'var(--color-muted)' }}>PDF, DOC, DOCX, XLS, XLSX, ZIP up to 25MB each</p>
            <input
              id="supportingUpload"
              type="file"
              multiple
              onChange={(e) => handleFileChange(e, 'supporting')}
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.rar"
            />
          </div>
          {supportingCount > 0 && <FileList files={form.supportingFiles} type="supporting" />}
        </div>

        {/* Warning if files missing */}
        {!canContinue && (
          <div className="p-4 rounded-lg" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }}>
            <p className="text-sm font-medium" style={{ color: '#92400E' }}>
              Both plans and photographs are required to proceed to the next step
            </p>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4">
          <button 
            onClick={onBack} 
            className="btn-secondary flex items-center gap-2" 
            data-testid="step2-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <button
            onClick={onContinue}
            disabled={!canContinue}
            className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="step2-continue-btn"
          >
            Continue
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
